import { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Card, Form, Table, Spinner, Button, ProgressBar } from "react-bootstrap";
import { BiRefresh, BiCart, BiReceipt, BiCreditCard, BiStore, BiDollar, BiPackage, BiTag, BiTrendingUp } from "react-icons/bi";
import {
  getShopSalesInsights,
  getRevenueInsights,
  getTechbazaarSalesInsights,
} from "../api/getapi";
import { formatNumber } from "../Utilities/numbers";
import { ECHART_COLORS } from "../Utilities/echartsTheme";
import StatCard from "../Components/DashboardCards/StatCard/StatCard";
import EChartsTimeSeriesLine from "../Components/Charts/EChartsTimeSeriesLine";
import EChartsHorizontalBar from "../Components/Charts/EChartsHorizontalBar";

const spinKeyframes = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 0.8s linear infinite; display: inline-block; }`;

const timePeriods = [
  { value: "all-time", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this-week", label: "This Week" },
  { value: "this-month", label: "This Month" },
  { value: "this-year", label: "This Year" },
  { value: "last-year", label: "Last Year" },
];

const SalesDashboard = () => {
  const [posSales, setPosSales] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState("all-time");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [posRes, revenueRes, subRes] = await Promise.all([
        getShopSalesInsights(),
        getRevenueInsights({ timePeriod }),
        getTechbazaarSalesInsights(),
      ]);
      setPosSales(posRes.data);
      setRevenue(revenueRes.data);
      setSubscriptions(subRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [timePeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const subscriptionData = subscriptions?.general?.subscriptionPaymentsReceived;

  const posAllTime = posSales?.general?.["all-time"];
  const posSalesCount = posAllTime?.total_sales ?? 0;
  const posSalesAmount = posAllTime?.total_sales_amount ?? 0;

  const trialToPaid = subscriptions?.trialToPaidPlan?.["all-time"] || 0;

  const salesTeamData = useMemo(() => {
    const raw = subscriptions?.registeredShopByUser?.["all-time"];
    if (!raw || !Array.isArray(raw)) return [];
    return raw
      .map((r) => ({ label: r.user_full_name, value: Number(r.total_regsitered_shops || 0) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [subscriptions]);

  const maxRevenue = useMemo(
    () => revenue?.perShop?.[0]?.revenue || 1,
    [revenue]
  );

  return (
    <Container fluid className="px-xl-5">
      <style>{spinKeyframes}</style>
      <Row className="py-2 align-items-center">
        <Col md="6">
          <h5 className="fw-bold">Sales Dashboard</h5>
          <p className="text-muted mb-0">Sales performance, revenue trends, and top performing shops.</p>
        </Col>
        <Col md="6" className="text-md-end mt-3 mt-md-0">
          <Button
            variant="light"
            size="sm"
            className="me-2 border"
            onClick={fetchData}
            disabled={loading}
          >
            <BiRefresh size={20} className={loading ? "spin" : ""} />
          </Button>
          <Form.Select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            style={{ width: 180, display: "inline-block" }}
          >
            {timePeriods.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {loading && (
        <div className="text-center mt-4">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && (
        <>
          {/* Primary KPI Stat Cards */}
          <Row className="g-3 mt-1 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Orders Value"
                count={`Rs. ${formatNumber(revenue?.general?.total_revenue || 0)}`}
                icon={<BiCart />}
                color={ECHART_COLORS.primary}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="POS Sales Count"
                count={formatNumber(posSalesCount)}
                icon={<BiReceipt />}
                color={ECHART_COLORS.success}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Subscription Payments"
                count={`Rs. ${formatNumber(subscriptionData?.["all-time"] || 0)}`}
                icon={<BiCreditCard />}
                color={ECHART_COLORS.purple}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Top Shop Orders Value"
                count={`Rs. ${formatNumber(revenue?.perShop?.[0]?.revenue || 0)}`}
                icon={<BiStore />}
                color={ECHART_COLORS.accentLight}
              />
            </Col>
          </Row>

          {/* Secondary KPI Stat Cards */}
          <Row className="g-3 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="POS Sales Amount"
                count={`Rs. ${formatNumber(posSalesAmount)}`}
                icon={<BiDollar />}
                color={ECHART_COLORS.emerald}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Total Delivery Charges"
                count={`Rs. ${formatNumber(revenue?.general?.total_delivery_charges || 0)}`}
                icon={<BiPackage />}
                color={ECHART_COLORS.teal}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Total Discounts Given"
                count={`Rs. ${formatNumber(revenue?.general?.total_discount || 0)}`}
                icon={<BiTag />}
                color={ECHART_COLORS.danger}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Trial → Paid Conversions"
                count={formatNumber(trialToPaid)}
                icon={<BiTrendingUp />}
                color={ECHART_COLORS.accent}
              />
            </Col>
          </Row>

          {/* Time Series Line Charts */}
          <Row className="g-3 mb-4">
            <Col md="12" lg="6" sm="12" xl="6">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">POS Sales — Monthly Trend</h6>
                <div style={{ height: 300 }}>
                  <EChartsTimeSeriesLine
                    data={posSales?.monthly || []}
                    label="POS Sales Count"
                    color={ECHART_COLORS.success}
                    valueKey="total_sales"
                  />
                </div>
              </Card>
            </Col>
            <Col md="12" lg="6" sm="12" xl="6">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Subscription Payments — Monthly Trend</h6>
                <div style={{ height: 300 }}>
                  <EChartsTimeSeriesLine
                    data={subscriptions?.monthlySubscriptionPayments || []}
                    label="Payments (Rs.)"
                    color={ECHART_COLORS.purple}
                    valueKey="total_payments"
                  />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Sales Team Performance */}
          <Row className="g-3 mb-4">
            <Col md="12" lg="6" sm="12" xl="6">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Shops Registered by Sales Team</h6>
                <div style={{ height: 300 }}>
                  {salesTeamData.length > 0 ? (
                    <EChartsHorizontalBar
                      data={salesTeamData}
                      label="Shops Registered"
                      multiColor
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                      No data available
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          </Row>

          {/* Top Shops by Orders Value Table */}
          <Row className="g-3 mb-4">
            <Col md="12">
              <Card className="border-0 shadow-sm p-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                  <h6 className="fw-bold m-0">Top Shops by Orders Value</h6>
                  <div className="d-flex flex-wrap gap-3 text-muted" style={{ fontSize: "0.85rem" }}>
                    <span><strong className="text-dark">{formatNumber(revenue?.general?.total_sources || 0)}</strong> Total Order Sources</span>
                    <span><strong className="text-success">Rs. {formatNumber(revenue?.general?.total_revenue || 0)}</strong> Total Value</span>
                  </div>
                </div>
                <div className="table-responsive">
                  <Table hover size="sm" className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-center" style={{ width: 40 }}>#</th>
                        <th>Shop</th>
                        <th className="text-end" style={{ width: 90 }} title="Number of order sources (shop-level order entries)">Order Sources</th>
                        <th className="text-end" style={{ width: 130 }}>Orders Value</th>
                        <th className="text-end" style={{ width: 110 }}>Delivery</th>
                        <th className="text-end" style={{ width: 110 }}>Discount</th>
                        <th className="text-end" style={{ width: 110 }}>Voucher</th>
                        <th style={{ width: 160 }} title="Orders value relative to the top shop">
                          % of Top
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(revenue?.perShop || [])
                        .slice(0, 20)
                        .map((row, i) => {
                          const pct = Math.round((Number(row.revenue) / maxRevenue) * 100);
                          return (
                            <tr key={row.shop_id}>
                              <td className="text-center text-muted">{i + 1}</td>
                              <td className="fw-medium">{row.shop_name}</td>
                              <td className="text-end">{formatNumber(row.source_count)}</td>
                              <td className="text-end fw-bold">Rs. {formatNumber(row.revenue)}</td>
                              <td className="text-end">Rs. {formatNumber(row.delivery_charges)}</td>
                              <td className="text-end text-danger">Rs. {formatNumber(row.discount)}</td>
                              <td className="text-end text-warning">Rs. {formatNumber(row.voucher_discount)}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <ProgressBar
                                    now={pct}
                                    variant={pct >= 75 ? "success" : pct >= 40 ? "primary" : "info"}
                                    style={{ height: 6, flex: 1 }}
                                  />
                                  <small className="text-muted" style={{ minWidth: 36 }}>{pct}%</small>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      {(!revenue?.perShop || revenue.perShop.length === 0) && (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-3">
                            No revenue data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default SalesDashboard;
